import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Container, Row, Col } from 'react-bootstrap';
import { 
  PlayFill, 
  CheckCircleFill, 
  Whatsapp, 
  TelephoneFill,
  Stars,
  ArrowLeft
} from 'react-bootstrap-icons';
import ProductCard from './ProductCard';
import productsData from '../products.json';
import './ProductDetailsSection.css';

// دالة مساعدة لتحويل النص لـ Slug (مثال: "كرسي فاخر" -> "كرسي-فاخر")
const createSlug = (text) => {
  return text
    ?.toString()
    ?.trim()
    ?.toLowerCase()
    ?.replace(/\s+/g, '-') // استبدال المسافات بـ -
    ?.replace(/[^\w\u0600-\u06FF-]+/g, ''); // الحفاظ على الحروف العربية والإنجليزي
};

const ProductDetailsSection = () => {
  // 🔹 نستقبل title أو slug بدلاً من id
  const { title } = useParams();

  // تجميع الأقسام والمنتجات
  const basic = productsData?.basic || {};
  const secondary = productsData?.secondary || {};
  const allCategories = useMemo(() => ({ ...basic, ...secondary }), [basic, secondary]);

  const allProducts = useMemo(() => {
    return Object.entries(allCategories).reduce((acc, [catKey, category]) => {
      const items = Array.isArray(category) ? category : category?.items || [];
      const itemsWithCategory = items.map(item => ({ ...item, categoryKey: catKey }));
      return [...acc, ...itemsWithCategory];
    }, []);
  }, [allCategories]);

  // 🔹 البحث بالـ slug أو العنوان بدلاً من الـ id
  const product = allProducts.find(p => {
    const productSlug = createSlug(p.slug || p.title);
    const paramSlug = createSlug(decodeURIComponent(title));
    return productSlug === paramSlug;
  });

  // State الميديا المعروضة حالياً
  const [selectedType, setSelectedType] = useState('image');
  const [selectedUrl, setSelectedUrl] = useState('');

  // 🌟 ضبط الصورة الأولى عند تغيير الـ title/slug 🌟
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (product && product.images && product.images.length > 0) {
      setSelectedType('image');
      setSelectedUrl(product.images[0]);
    }
  }, [title, product]); // التحديث عند تغيير عنوان المنتج

  const relatedProducts = allProducts
    .filter(p => p.categoryKey === product?.categoryKey && p.id !== product?.id)
    .slice(0, 3);

  if (!product) {
    return (
      <Container className="text-center py-5 my-5">
        <h2>المنتج غير موجود</h2>
        <Link to="/" className="btn btn-outline-primary mt-3">العودة للرئيسية</Link>
      </Container>
    );
  }

  const whatsappMessage = encodeURIComponent(
    `مرحباً، أود الاستفسار عن تفاصيل ومواصفات منتج: ${product.title} ${product.code ? `(كود: ${product.code})` : ''}`
  );

  return (
    <div className="pro-details-page py-4 py-lg-5" dir="rtl">
      <Container>

        {/* الكارت الرئيسي */}
        <div className="pro-main-card p-4 p-lg-5 mb-5 position-relative">
          
          {/* هيدر الكارت الداخلي */}
          <div className="d-flex justify-content-between align-items-center mb-4 pb-3 border-bottom border-light-subtle">
            <span className="pro-badge-royal">
              <Stars size={15} /> ضمان وجودة عالية
            </span>
            
            <Link to="/" className="pro-back-btn">
              <span>العودة لجميع المنتجات</span>
              <ArrowLeft size={16} />
            </Link>
          </div>

          <Row className="g-4 g-lg-5 align-items-center">
            
            {/* 🎥 مسرح الصور والفيديو الرئيسي */}
            <Col lg={6}>
              <div className="pro-media-wrapper">
                
                {/* شاشة العرض الرئيسية */}
                <div className="pro-viewport mb-3">
                  {selectedType === 'video' ? (
                    <video 
                      key={selectedUrl} 
                      src={selectedUrl} 
                      controls 
                      autoPlay 
                      className="pro-media-element" 
                      loading="lazy"
                    />
                  ) : (
                    <img 
                      key={selectedUrl} 
                      src={selectedUrl} 
                      alt={product.title} 
                      className="pro-media-element continuous-deep-zoom" 
                      loading="lazy"
                    />
                  )}

                  {product.code && (
                    <span className="pro-code-badge">كود: {product.code}</span>
                  )}
                </div>

                {/* مصغرات التبديل */}
                <div className="pro-thumbs-grid d-flex gap-2 flex-wrap justify-content-start">
                  {/* أزرار الصور */}
                  {product.images?.map((img, idx) => (
                    <button
                      key={idx}
                      type="button"
                      className={`pro-thumb ${selectedType === 'image' && selectedUrl === img ? 'active' : ''}`}
                      onClick={() => {
                        setSelectedType('image');
                        setSelectedUrl(img);
                      }}
                    >
                      <img src={img} alt={`thumb-${idx}`} loading="lazy"/>
                    </button>
                  ))}

                  {/* زر الفيديو */}
                  {product.video && (
                    <button
                      key="video-thumb"
                      type="button"
                      className={`pro-thumb video-highlight ${selectedType === 'video' ? 'active' : ''}`}
                      onClick={() => {
                        setSelectedType('video');
                        setSelectedUrl(product.video);
                      }}
                    >
                      <PlayFill size={24} className="play-ico" />
                      <span className="v-text">فيديو</span>
                    </button>
                  )}
                </div>

              </div>
            </Col>

            {/* 📝 بيانات المنتج */}
            <Col lg={6}>
              <div className="pro-info-wrapper text-start">
                <h1 className="pro-title mb-3">{product.title}</h1>
                <p className="pro-desc text-muted mb-4">{product.description}</p>

                {/* المواصفات والمميزات */}
                {product.features && product.features.length > 0 && (
                  <div className="pro-specs mb-4">
                    <h5 className="fw-bold mb-3 text-navy">المواصفات والمميزات:</h5>
                    <div className="d-flex flex-column gap-2">
                      {product.features.map((feature, idx) => (
                        <div key={idx} className="pro-spec-item">
                          <CheckCircleFill className="text-primary mt-1 flex-shrink-0" size={16} />
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* كارت طلب الاستفسار والواتساب */}
                <div className="pro-order-card p-4 rounded-4 mt-4">
                  <h6 className="fw-bold text-navy mb-1">هل تريد طلب هذا المنتج أو الاستفسار؟</h6>
                  <p className="small text-muted mb-3">تواصل معنا فوراً للحصول على السعر المناسب للمقاس المطلوب.</p>

                  <div className="d-flex gap-3 flex-column flex-sm-row">
                    <a
                      href={`https://wa.me/966504480025?text=${whatsappMessage}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-pro-wa"
                    >
                      <Whatsapp size={20} />
                      <span>تواصل عبر الواتساب</span>
                    </a>

                    <a href="tel:966504480025" className="btn-pro-call">
                      <TelephoneFill size={18} />
                      <span>اتصال مباشر</span>
                    </a>
                  </div>
                </div>

              </div>
            </Col>

          </Row>
        </div>

        {/* 🌟 منتجات ذات صلة 🌟 */}
        {relatedProducts.length > 0 && (
          <div className="related-products-section pt-4 mt-5 text-start">
            <div className="d-flex align-items-center justify-content-between mb-4">
              <h3 className="fw-bold text-navy m-0 fs-4">منتجات قد تعجبك أيضاً</h3>
              <Link to="/#products" className="see-more-link d-flex align-items-center gap-1">
                <span>عرض الكل</span>
                <ArrowLeft size={16} />
              </Link>
            </div>

            <Row className="g-4">
              {relatedProducts.map((relProduct, idx) => (
                <Col key={relProduct.id || idx} md={6} lg={4}>
                  <ProductCard {...relProduct} />
                </Col>
              ))}
            </Row>
          </div>
        )}

      </Container>
    </div>
  );
};

export default ProductDetailsSection;